import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Spin,
  Space,
  Button,
  Select,
  Modal,
  DatePicker,
  Timeline,
  Avatar,
  Alert,
  Row,
  Col,
  Affix,
  Flex,
  Empty,
} from 'antd';
import {
  CommentOutlined,
  CloudUploadOutlined,
  FormOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  ReloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import WorkspaceFeed from './WorkspaceFeed';
import WorkspaceFeedItem from './WorkspaceFeedItem';
import WorkspaceEventComposer from './WorkspaceEventComposer';
import WorkspaceMediaPreviewModal from './WorkspaceMediaPreviewModal';
import { WORKSPACE_EVENT_TYPES } from './workspaceEventConstants';
import { mapPortalFeedRows } from './utils/buildWorkspaceFeed';
import customerService from '../../services/customerService';

import './workspace.css';

const { Title, Text } = Typography;

const DEFAULT_EMPTY =
  'No messages yet. You can upload files or leave a note for the business here.';

const FEED_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'comments', label: 'Comments' },
  { key: 'uploads', label: 'Uploads' },
  { key: 'requests', label: 'Requests' },
  { key: 'status', label: 'Status updates' },
];

const DATE_PRESETS = {
  ALL: 'all',
  TODAY: 'today',
  LAST_5_DAYS: 'last_5_days',
  SPECIFIC: 'specific',
};

const DATE_PRESET_OPTIONS = [
  { value: DATE_PRESETS.ALL, label: 'All' },
  { value: DATE_PRESETS.TODAY, label: 'Today' },
  { value: DATE_PRESETS.LAST_5_DAYS, label: 'Last 5 days' },
];

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildDateRange(preset, specificDay) {
  if (preset === DATE_PRESETS.ALL) return null;
  if (preset === DATE_PRESETS.SPECIFIC) {
    if (!specificDay || !dayjs(specificDay).isValid()) return null;
    const d = dayjs(specificDay).startOf('day');
    return [d, d.endOf('day')];
  }
  const now = dayjs();
  if (preset === DATE_PRESETS.TODAY) return [now.startOf('day'), now.endOf('day')];
  if (preset === DATE_PRESETS.LAST_5_DAYS) return [now.subtract(4, 'day').startOf('day'), now.endOf('day')];
  return null;
}

function rowMatchesFilter(row, filterKey) {
  if (filterKey === 'all') return true;
  if (row.kind !== 'workspace_event') return filterKey === 'uploads';
  const t = row.data?.type || WORKSPACE_EVENT_TYPES.MESSAGE;
  if (filterKey === 'comments') return t === WORKSPACE_EVENT_TYPES.MESSAGE;
  if (filterKey === 'uploads') return t === WORKSPACE_EVENT_TYPES.UPLOAD;
  if (filterKey === 'requests') {
    return (
      t === WORKSPACE_EVENT_TYPES.REVISION_REQUEST ||
      t === WORKSPACE_EVENT_TYPES.DOCUMENT_REQUEST ||
      t === WORKSPACE_EVENT_TYPES.CONFIRMATION_REQUEST
    );
  }
  if (filterKey === 'status') {
    return t === WORKSPACE_EVENT_TYPES.STATUS_CHANGE || t === WORKSPACE_EVENT_TYPES.SYSTEM;
  }
  return true;
}

function timelineDot(item) {
  const wrap = (suffix, icon) => (
    <Avatar
      size="small"
      icon={icon}
      className={`workspace-admin-timeline-avatar workspace-admin-timeline-avatar--${suffix}`}
    />
  );
  if (item.kind !== 'workspace_event') {
    return wrap('upload', <CloudUploadOutlined style={{ fontSize: 14 }} />);
  }
  const t = item.data?.type || WORKSPACE_EVENT_TYPES.MESSAGE;
  if (t === WORKSPACE_EVENT_TYPES.MESSAGE)            return wrap('message',      <CommentOutlined    style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.UPLOAD)             return wrap('upload',       <CloudUploadOutlined style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.REVISION_REQUEST)   return wrap('revision',     <SyncOutlined       style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.DOCUMENT_REQUEST)   return wrap('document',     <FormOutlined       style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.CONFIRMATION_REQUEST) return wrap('confirmation', <FormOutlined     style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.APPROVAL)           return wrap('approval',     <CheckCircleOutlined style={{ fontSize: 14 }} />);
  if (t === WORKSPACE_EVENT_TYPES.STATUS_CHANGE || t === WORKSPACE_EVENT_TYPES.SYSTEM)
    return wrap('status', <InfoCircleOutlined style={{ fontSize: 14 }} />);
  return wrap('message', <CommentOutlined style={{ fontSize: 14 }} />);
}

export function workspaceEventSubscribeNoOp(_refresh) {
  return () => {};
}

/**
 * Customer portal workspace events panel.
 *
 * panelLayout:
 *   "card"     — Card-wrapped sidebar embed (default)
 *   "rail"     — Full-height scrolling feed with docked composer
 *   "timeline" — Full-page timeline with filters, matching admin workspace design
 */
const WorkspaceEventsPanel = ({
  entityPublicId,
  documentType,
  panelLayout = 'card',
  config = {},
  subscribeWorkspaceEvent = workspaceEventSubscribeNoOp,
  onUnreadCountChange,
}) => {
  const {
    canUpload = true,
    canComment = true,
    showCustomerVisibilityToggle = false,
    emptyStateDescription = DEFAULT_EMPTY,
    loadFeed,
    portalHandlers,
    portalWorkspace = null,
  } = config;

  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [replyContext, setReplyContext] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const composerRef = useRef(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  // Timeline-mode filter state
  const [feedFilter, setFeedFilter] = useState('all');
  const [datePreset, setDatePreset] = useState(DATE_PRESETS.ALL);
  const [specificDate, setSpecificDate] = useState(null);
  const [specificDateModalOpen, setSpecificDateModalOpen] = useState(false);
  const [specificDateDraft, setSpecificDateDraft] = useState(() => dayjs().startOf('day'));

  const timelineFeedEndRef = useRef(null);
  const didInitialScrollRef = useRef(false);

  const railFeedEndRef = useRef(null);
  const railDidInitialScrollRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!entityPublicId || typeof loadFeed !== 'function') return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await loadFeed();
      const payload = res?.data?.data ?? res?.data;
      const rows = payload?.feed ?? [];
      setFeedItems(mapPortalFeedRows(rows));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoadError('You do not have permission to view this workspace.');
      } else if (status === 404) {
        setLoadError('This document was not found.');
      } else {
        setLoadError('Could not load workspace. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [entityPublicId, loadFeed]);

  const onWorkspaceEventReply = useMemo(() => {
    if (typeof portalHandlers?.postWorkspaceEventReply !== 'function') return null;
    return async (payload) => {
      await portalHandlers.postWorkspaceEventReply(payload);
      await refresh();
    };
  }, [portalHandlers, refresh]);

  const handleOpenReply = useCallback(({ workspaceEventPublicId, actorName }) => {
    setReplyContext({ workspaceEventPublicId, actorName });
    composerRef.current?.focusTextarea?.();
  }, []);

  const handleMarkRead = useCallback(async (workspaceEventPublicId) => {
    if (!documentType || !entityPublicId) return;
    try {
      await customerService.markEventRead(documentType, entityPublicId, workspaceEventPublicId);
    } catch { /* read tracking is best-effort — silent */ }
  }, [documentType, entityPublicId]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    return subscribeWorkspaceEvent(() => { void refreshRef.current?.(); });
  }, [subscribeWorkspaceEvent]);

  const dateRange = useMemo(
    () => buildDateRange(datePreset, specificDate),
    [datePreset, specificDate],
  );

  // Customers cannot bootstrap a workspace event on an invoice/estimate that has
  // no posts yet — the workspace must be opened by staff first. On workspace
  // pages (portalWorkspace set), this lock does not apply.
  const composerLockedReason =
    !portalWorkspace && feedItems.length === 0 && !loading
      ? 'Replies are available once your account manager opens this workspace.'
      : null;

  const filteredItems = useMemo(() => {
    return feedItems.filter((row) => {
      if (dateRange) {
        const [start, end] = dateRange;
        const at = row.occurredAt ? dayjs(row.occurredAt) : null;
        if (at) {
          const t = at.valueOf();
          if (t < start.valueOf() || t > end.valueOf()) return false;
        }
      }
      return rowMatchesFilter(row, feedFilter);
    });
  }, [feedItems, feedFilter, dateRange]);

  // Authoritative participants from the workspace API when available; fall back to
  // names seen in the feed for legacy invoice/estimate views.
  const portalParticipants = useMemo(() => {
    if (Array.isArray(portalWorkspace?.participants)) {
      return portalWorkspace.participants.map((p) => ({
        name: p.name,
        role: p.role || null,
        kind: p.kind || 'staff',
        profileUrl: null,
      }));
    }
    const seen = new Map();
    for (const row of feedItems) {
      if (row.kind !== 'workspace_event') continue;
      const ev = row.data;
      const creator = ev?.extra?.creator;
      const portal = ev?.extra?.portal_author;
      if (creator) {
        const name =
          creator.extra?.full_name ||
          [creator.first_name, creator.last_name].filter(Boolean).join(' ').trim();
        if (name && !seen.has(name)) {
          seen.set(name, { name, profileUrl: creator.extra?.profile_image || null });
        }
      }
      if (portal?.full_name && !seen.has(portal.full_name)) {
        seen.set(portal.full_name, { name: portal.full_name, profileUrl: null });
      }
    }
    return [...seen.values()];
  }, [feedItems, portalWorkspace]);

  // Unread = workspace events created by staff (no portal_author) that the customer hasn't viewed yet.
  const unreadCount = useMemo(
    () =>
      feedItems.reduce((acc, row) => {
        if (row.kind !== 'workspace_event') return acc;
        const d = row.data;
        const isAdminCreated = !d?.extra?.portal_author;
        const isUnread = isAdminCreated && d?.is_read_by_customer === false;
        return acc + (isUnread ? 1 : 0);
      }, 0),
    [feedItems],
  );

  useEffect(() => {
    if (typeof onUnreadCountChange === 'function') {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  // All media across all workspace events (root + reply attachments)
  const allMedia = useMemo(() => {
    return feedItems.flatMap((row) => {
      if (row.kind === 'workspace_event') {
        const rootMedia = Array.isArray(row.data?.media) ? row.data.media : [];
        const replyMedia = (Array.isArray(row.data?.replies) ? row.data.replies : [])
          .flatMap((r) => (Array.isArray(r?.media) ? r.media : []));
        return [...rootMedia, ...replyMedia];
      }
      if (row.kind === 'file') return row.data ? [row.data] : [];
      return [];
    });
  }, [feedItems]);

  useEffect(() => {
    if (panelLayout !== 'rail') return;
    if (loading) return;
    if (!feedItems.length) return;
    const el = railFeedEndRef.current;
    if (!el) return;
    const behavior = railDidInitialScrollRef.current ? 'smooth' : 'auto';
    railDidInitialScrollRef.current = true;
    requestAnimationFrame(() => { el.scrollIntoView({ behavior, block: 'end' }); });
  }, [panelLayout, loading, feedItems.length]);

  useEffect(() => {
    if (panelLayout !== 'timeline') return;
    if (loading) return;
    if (!filteredItems.length) return;
    const el = timelineFeedEndRef.current;
    if (!el) return;
    const behavior = didInitialScrollRef.current ? 'smooth' : 'auto';
    didInitialScrollRef.current = true;
    requestAnimationFrame(() => { el.scrollIntoView({ behavior, block: 'end' }); });
  }, [panelLayout, loading, filteredItems.length, feedFilter, datePreset, specificDate]);

  const dateSelectOptions = useMemo(
    () => [
      ...DATE_PRESET_OPTIONS,
      {
        value: DATE_PRESETS.SPECIFIC,
        label: specificDate
          ? `Date: ${dayjs(specificDate).format('MMM D, YYYY')}`
          : 'Choose date…',
      },
    ],
    [specificDate],
  );

  const handleDatePresetChange = useCallback((value) => {
    if (value === DATE_PRESETS.SPECIFIC) return;
    setDatePreset(value);
    setSpecificDate(null);
  }, []);

  const handleDateSelect = useCallback((value) => {
    if (value !== DATE_PRESETS.SPECIFIC) return;
    setSpecificDateDraft(
      specificDate && dayjs(specificDate).isValid()
        ? dayjs(specificDate).startOf('day')
        : dayjs().startOf('day'),
    );
    setSpecificDateModalOpen(true);
  }, [specificDate]);

  const handleDateModalOk = useCallback(() => {
    const d = specificDateDraft && dayjs(specificDateDraft).isValid()
      ? dayjs(specificDateDraft).startOf('day')
      : null;
    if (!d) return;
    setSpecificDate(d);
    setDatePreset(DATE_PRESETS.SPECIFIC);
    setSpecificDateModalOpen(false);
  }, [specificDateDraft]);

  const previewModal = (
    <WorkspaceMediaPreviewModal
      open={previewOpen}
      media={previewMedia}
      onClose={() => { setPreviewOpen(false); setPreviewMedia(null); }}
    />
  );

  const openPreview = (m) => { setPreviewMedia(m); setPreviewOpen(true); };

  // ── Timeline (full-page) layout ──────────────────────────────────────────
  if (panelLayout === 'timeline') {
    const mainContent = (
      <>
        <Affix offsetTop={64}>
          <div className="portal-workspace-timeline__toolbar portal-workspace-timeline__toolbar--affixed">
            <Space wrap size={[12, 8]} align="center">
              <Space size="small" align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>Type</Text>
                <Select
                  size="small"
                  value={feedFilter}
                  onChange={setFeedFilter}
                  options={FEED_FILTERS.map((f) => ({ value: f.key, label: f.label }))}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: 150 }}
                />
              </Space>
              <Space size="small" align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>Date</Text>
                <Select
                  size="small"
                  value={datePreset}
                  onChange={handleDatePresetChange}
                  onSelect={handleDateSelect}
                  options={dateSelectOptions}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: 150 }}
                />
              </Space>
            </Space>
            <Button
              variant="filled"
              color="default"
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => void refresh()}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
        </Affix>

        <div className="portal-workspace-timeline__feed">
          {loadError ? (
            <Alert
              type="error"
              title={loadError}
              showIcon
              style={{ margin: 'var(--ant-padding)' }}
              action={
                <Button size="small" onClick={() => void refresh()}>
                  Retry
                </Button>
              }
            />
          ) : (
            <Spin spinning={loading}>
              {filteredItems.length === 0 ? (
                <div className="portal-workspace-timeline__empty">
                  <Text type="secondary">{emptyStateDescription}</Text>
                </div>
              ) : (
                <Timeline mode="start" className="portal-workspace-timeline workspace-admin-timeline">
                  {filteredItems.map((row, idx) => (
                    <Timeline.Item
                      key={`${row.kind}-${row.data?.id ?? idx}`}
                      icon={timelineDot(row)}
                      color="gray"
                    >
                      <WorkspaceFeedItem
                        item={row}
                        layout="timeline"
                        onPreviewFile={openPreview}
                        onWorkspaceEventReply={onWorkspaceEventReply}
                        onOpenReply={handleOpenReply}
                        onMarkRead={handleMarkRead}
                      />
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
              <div ref={timelineFeedEndRef} aria-hidden />
            </Spin>
          )}
        </div>

        <div className="portal-workspace-timeline__composer">
          <WorkspaceEventComposer
            ref={composerRef}
            entityType="portal"
            entityPublicId={entityPublicId}
            canComment={canComment}
            canUpload={canUpload}
            showCustomerVisibilityToggle={showCustomerVisibilityToggle}
            variant="portal"
            portalHandlers={portalHandlers}
            onSubmitted={refresh}
            layout="rail"
            replyContext={replyContext}
            onClearReplyContext={() => setReplyContext(null)}
            lockedReason={composerLockedReason}
          />
        </div>
      </>
    );

    const sidebarContent = portalWorkspace ? (
      <Affix offsetTop={64}>
        <Space orientation="vertical" size="middle" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {portalWorkspace.summarySlot ?? null}

          <Card size="small" title="Participants">
            {portalParticipants.length ? (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                {portalParticipants.map((p, idx) => (
                  <Flex key={`${p.name}-${idx}`} align="center" gap="small">
                    <Avatar size={24} src={p.profileUrl || undefined}>
                      {(p.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Text style={{ fontSize: 13 }} ellipsis>{p.name}</Text>
                  </Flex>
                ))}
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 13 }}>No activity yet.</Text>
            )}
          </Card>

          <Card size="small" title={`Files (${allMedia.length})`}>
            {allMedia.length ? (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                {allMedia.slice(0, 8).map((m, idx) => (
                  <button
                    type="button"
                    key={m.id ?? idx}
                    onClick={() => openPreview(m)}
                    className="portal-workspace-sidebar-attachment"
                    title={m.display_name || m.name || 'File'}
                  >
                    <Flex gap="small" style={{ minWidth: 0, flex: 1 }} align="center">
                      <PaperClipOutlined style={{ flexShrink: 0 }} />
                      <Text ellipsis style={{ maxWidth: 140, fontSize: 13 }}>
                        {m.display_name || m.name || 'File'}
                      </Text>
                    </Flex>
                    {m.file_size ? (
                      <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                        {formatFileSize(m.file_size)}
                      </Text>
                    ) : null}
                  </button>
                ))}
                {allMedia.length > 8 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    +{allMedia.length - 8} more
                  </Text>
                ) : null}
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 13 }}>No files shared yet.</Text>
            )}
          </Card>
        </Space>
      </Affix>
    ) : null;

    return (
      <>
        <div className="portal-workspace-timeline-root">
          {portalWorkspace ? (
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16} className="portal-workspace-timeline__main">
                {mainContent}
              </Col>
              <Col xs={24} lg={8} className="portal-workspace-timeline__sidebar">
                {sidebarContent}
              </Col>
            </Row>
          ) : (
            mainContent
          )}
        </div>

        <Modal
          title="Choose date"
          open={specificDateModalOpen}
          onOk={handleDateModalOk}
          onCancel={() => setSpecificDateModalOpen(false)}
          destroyOnHidden
          okText="Apply"
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Show activity from this calendar day only.
          </Text>
          <DatePicker
            value={specificDateDraft}
            onChange={(d) => setSpecificDateDraft(d ? d.startOf('day') : dayjs().startOf('day'))}
            style={{ width: '100%' }}
            allowClear={false}
          />
        </Modal>

        {previewModal}
      </>
    );
  }

  // ── Rail layout (right-panel on detail pages) ────────────────────────────
  if (panelLayout === 'rail') {
    return (
      <>
        <div className="workspace-rail-root">
          <Spin spinning={loading} wrapperClassName="workspace-rail__spin">
            {feedItems.length === 0 ? (
              <div className="workspace-feed workspace-feed--rail workspace-feed--rail-empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  styles={{ image: { height: 56 } }}
                  description={
                    <Text type="secondary">{emptyStateDescription}</Text>
                  }
                />
              </div>
            ) : (
              <div className="workspace-feed workspace-feed--rail">
                <Timeline
                  mode="start"
                  className="workspace-admin-timeline workspace-rail-timeline"
                >
                  {feedItems.map((row, idx) => (
                    <Timeline.Item
                      key={`${row.kind}-${row.data?.id ?? idx}`}
                      icon={timelineDot(row)}
                      color="gray"
                    >
                      <WorkspaceFeedItem
                        item={row}
                        layout="timeline"
                        onPreviewFile={openPreview}
                        onWorkspaceEventReply={onWorkspaceEventReply}
                        onOpenReply={handleOpenReply}
                        onMarkRead={handleMarkRead}
                      />
                    </Timeline.Item>
                  ))}
                </Timeline>
                <div ref={railFeedEndRef} aria-hidden />
              </div>
            )}
          </Spin>
          <div className="workspace-rail__composer">
            <WorkspaceEventComposer
              ref={composerRef}
              entityType="portal"
              entityPublicId={entityPublicId}
              canComment={canComment}
              canUpload={canUpload}
              showCustomerVisibilityToggle={showCustomerVisibilityToggle}
              variant="portal"
              portalHandlers={portalHandlers}
              onSubmitted={refresh}
              layout="rail"
              replyContext={replyContext}
              onClearReplyContext={() => setReplyContext(null)}
              lockedReason={composerLockedReason}
            />
          </div>
        </div>
        {previewModal}
      </>
    );
  }

  // ── Card layout (default sidebar embed) ─────────────────────────────────
  return (
    <>
      <Card
        loading={loading}
        size="default"
        className="workspace-feed-card"
        title={<Title level={5} style={{ margin: 0 }}>Workspace</Title>}
        styles={{ body: { padding: 'var(--ant-padding)', minHeight: 200 } }}
        style={{ border: '1px solid var(--varexe-border-color)', boxShadow: 'none' }}
      >
        <WorkspaceFeed
          items={feedItems}
          emptyDescription={emptyStateDescription}
          onPreviewFile={openPreview}
          onWorkspaceEventReply={onWorkspaceEventReply}
        />
        <WorkspaceEventComposer
          entityType="portal"
          entityPublicId={entityPublicId}
          canComment={canComment}
          canUpload={canUpload}
          showCustomerVisibilityToggle={showCustomerVisibilityToggle}
          variant="portal"
          portalHandlers={portalHandlers}
          onSubmitted={refresh}
          lockedReason={composerLockedReason}
        />
      </Card>
      {previewModal}
    </>
  );
};

export default WorkspaceEventsPanel;
